import _sortBy from 'lodash.sortby';
import { inject as service } from '@ember/service';
import { alias, and, bool, empty, equal, gt, lt, not, or, sort } from '@ember/object/computed';
import { computed } from '@ember/object';
import { isBlank, isEmpty, isNone, isPresent } from '@ember/utils';
import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import moment from 'moment-timezone';
import { modelApiAction } from 'cio/utils/model-api-actions';

import { belongsTo, hasMany } from 'ember-data/relationships';
import isValidConversion from 'cio/utils/is-valid-conversion';
import getPeopleFilters from 'cio/utils/get-people-filters';
import getUnsubscribedFilters from 'cio/utils/get-unsubscribed-filters';
import { ONE_DAY_IN_SECONDS, ONE_HOUR_IN_SECONDS, ONE_MINUTE_IN_SECONDS } from 'cio/utils/time-constants';
import { queryEncode } from 'cio/helpers/query';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import ScheduleModel from 'cio/models/schedule';
import TagModel from 'cio/models/tag';
import channelInfo from 'cio/utils/newsletter-channel-info';
import TemplateModel from 'cio/models/template';
import SegmentModel from 'cio/models/segment';
import UserOtherAccountModel from 'cio/models/user-other-account';
import COLOR_LABELS from 'cio/utils/color-labels';

import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';

import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import {
  getDefaultTemplateForTestGroup,
  getTestGroupIds,
  getTestGroupTemplates,
} from 'cio/utils/newsletter-testgroups';
import orderBy from 'lodash.orderby';
import {
  ConversionType,
  isConversionEventBased,
  isConversionSegmentBased,
  ModelWithConversion,
} from 'cio/utils/conversions';
import { Filters } from 'cio/utils/filters';
import SubscriptionCenterService from 'cio/services/subscription-center';
import { getSubscriptionTopicLegacyFilters } from 'cio/utils/get-subscription-topic-filters';
import isDestroyed from 'cio/utils/is-destroyed';

export type SendStateType = 'drafted' | 'scheduled' | 'sending' | 'sent' | 'stopped';
export type ChannelType = 'email' | 'in_app' | 'push' | 'twilio' | 'webhook' | 'unset';
export type UpdateType = 'tracking' | 'recipients' | 'channel' | 'test' | 'end_test' | 'send' | 'schedule' | 'cancel';
export type TestMetricType = keyof typeof VALID_TEST_METRICS_MAP;

export enum Channel {
  Email = 'email',
  Push = 'push',
  Twilio = 'twilio',
  Webhook = 'webhook',
  Unset = 'unset',
  InApp = 'in_app',
}

export enum Platform {
  All = 'all',
  iOS = 'ios',
  Android = 'android',
}

const VALID_TEST_METRICS_MAP = {
  clicked: 'click',
  opened: 'open',
  converted: 'conversion',
};

export const VALID_TEST_METRICS = Object.keys(VALID_TEST_METRICS_MAP);

export default class NewsletterModel extends Model implements ModelWithConversion {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;
  @service subscriptionCenter!: SubscriptionCenterService;

  @attr('string') name!: string;
  @attr('boolean', { defaultValue: true }) tracked!: boolean;
  @attr('string') channel!: ChannelType;

  // nullable properties
  @attr('base64-json') filters!: SerializedLegacyFilter[];

  @attr('number') attempted!: number;
  @attr('number') totalSent!: number;
  @attr('number') totalDelivered!: number;
  @attr('number') sendsCompleted!: number;
  @attr('number') created!: number;
  @attr('number') updated!: number;
  @attr('boolean') sending!: boolean;
  @attr('number') sentAt!: number;
  @attr('number') cancelledAt!: number;
  @attr('string') updateType!: UpdateType;
  @attr('boolean', { defaultValue: false }) sendToUnsubscribed!: boolean;
  @attr('boolean', { defaultValue: false }) useMessageLimits!: boolean;
  @attr('boolean', { allowNull: true }) sendToEveryone!: boolean;
  @attr('string') subscriptionTopicId!: string | null;

  @attr('boolean') deduped!: boolean;

  @attr('number') rateLimitEmailRate!: number;
  @attr('number') rateLimitTimePeriod!: number; // in seconds
  @and('rateLimitEmailRate', 'rateLimitTimePeriod')
  rateLimitEnabled!: boolean;

  @computed('rateLimitEmailRate', 'rateLimitTimePeriod', 'recipientsCount')
  get rateLimitedSendDuration() {
    let { rateLimitEmailRate, rateLimitTimePeriod, recipientsCount } = this;

    let unableToCompute =
      isNone(rateLimitEmailRate) || rateLimitEmailRate <= 0 || isNone(rateLimitTimePeriod) || recipientsCount === 0;
    let durationInSeconds;

    if (unableToCompute) {
      return;
    }

    // The first batch sends immediately, so we subtract the selected time period from the total value
    durationInSeconds = (recipientsCount / rateLimitEmailRate) * rateLimitTimePeriod - rateLimitTimePeriod;
    durationInSeconds = durationInSeconds <= 0 ? 0 : durationInSeconds;

    // returns the duration rounded up to the next value divisible by the selected rate limit period
    return moment.duration(Math.ceil(durationInSeconds / rateLimitTimePeriod) * rateLimitTimePeriod, 's');
  }
  @computed('rateLimitTimePeriod')
  get rateLimitTimePeriodUnit() {
    switch (this.rateLimitTimePeriod) {
      case ONE_MINUTE_IN_SECONDS:
        return 'minute';
      case ONE_HOUR_IN_SECONDS:
        return 'hour';
      case ONE_DAY_IN_SECONDS:
        return 'day';
      default:
        return undefined;
    }
  }

  @attr('string') testMetric!: TestMetricType;

  @computed('testMetric')
  get testMetricReadable() {
    return VALID_TEST_METRICS_MAP[this.testMetric];
  }

  @attr('number') testDuration!: number;
  @attr('number') testEndedAt!: number;
  @attr('boolean') automaticWinner!: boolean;

  @computed('splitTestActive', 'sendPercentage', 'automaticWinner')
  get hasTestSetup() {
    return this.splitTestActive || this.sendPercentage < 100 || this.automaticWinner;
  }

  @or('testEndedAt', 'sentToRemainingAt') testEnded!: number | boolean;

  @not('testEnded') testRunning!: boolean;

  @computed('subscriptionTopicId', 'subscriptionCenter.{initialized,topics.[]}')
  get subscriptionTopic() {
    if (this.subscriptionTopicId == null) {
      return null;
    }

    return this.subscriptionCenter.topics.findBy('id', this.subscriptionTopicId) ?? null;
  }

  @computed('subscriptionTopicId', 'subscriptionTopic')
  get hasDeletedSubscriptionTopic() {
    return !isEmpty(this.subscriptionTopicId) && this.subscriptionTopic == null;
  }

  // When a newsletter's "send to remaining" period starts, the backend updates
  // the newsletter's "scheduledAt" field. We'll use this to determine when the
  // test might end.
  @computed('scheduledSendToRemaining', 'scheduledAt', 'testDuration')
  get estimatedTestEndingAt() {
    let { scheduledAt, testDuration } = this;

    if (!scheduledAt) {
      return null;
    }

    if (this.scheduledSendToRemaining) {
      return scheduledAt;
    }

    return scheduledAt + testDuration;
  }

  @computed('estimatedTestEndingAt')
  get estimatedTestEndingInSeconds() {
    if (!this.estimatedTestEndingAt) {
      return;
    }

    return this.estimatedTestEndingAt - moment().unix();
  }

  @and('testRunning', 'hasSamplePercentage') testRunningWithSamplePercentage!: boolean;

  @computed('sentAt', 'testEndedAt')
  get testEndedInSeconds() {
    return this.testEndedAt - this.sentAt;
  }

  @belongsTo('template', { async: true, inverse: null }) winnerTemplate!: TemplateModel;

  @computed('winnerTemplate.id', 'testVariations.[]')
  get winnerTemplateIndex() {
    return (
      _sortBy(this.testVariations, ['created'])
        .mapBy('id')
        // @ts-ignore: nested key look-up on untyped property
        // eslint-disable-next-line ember/no-get
        .indexOf(this.get('winnerTemplate.id'))
    );
  }

  @attr('boolean') scheduledSendToRemaining!: boolean;
  // sentToRemaining can be different from when the test ends. It's the
  // timestamp when the winning variation was actually sent.
  @attr('number') sentToRemainingAt!: number;
  @bool('sentToRemainingAt') sentToRemaining!: boolean;

  // If a newsletter is sent but there's also a schedule, that means that a
  // winner is scheduled to be sent in the future. When a winner is scheduled,
  // the newsletter's `scheduledAt` is overwritten.
  @computed('isSent', 'sentToRemaining', 'scheduledAt')
  get scheduledForRemaining() {
    return this.isSent && !this.sentToRemaining && this.scheduledAt;
  }

  @attr('boolean', { allowNull: true, defaultValue: true }) conversion!: boolean | null;
  @attr('string', { defaultValue: ConversionType.PerformEvent }) conversionType!: ConversionType | '';
  @computed('conversionType')
  get isEventConversion(): boolean {
    return isConversionEventBased(this);
  }
  @computed('conversionType')
  get isSegmentConversion(): boolean {
    return isConversionSegmentBased(this);
  }
  @attr('string') conversionSegmentId!: string;
  @attr('string') conversionAction!: string;
  @attr('string') conversionEventName!: string;
  @attr('number') conversionWindow!: number;
  @attr() conversionEventFilters!: Filters | null;

  @attr('number') originalScheduledAt!: number | null;
  @attr('number') originalRecipientCount!: number;
  @attr('number') scheduledAt!: number | null;
  @attr('string') timezone!: string | null;
  @attr('boolean') tzMatchEnabled!: boolean;

  @attr('number', { defaultValue: 100 }) sendPercentage!: number;

  @computed('sendPercentage')
  get remainingPercentage() {
    return 100 - this.sendPercentage;
  }

  @computed('deduped', 'channel')
  get isDeduped() {
    if (this.deduped && (this.channel === 'email' || this.channel === 'unset')) {
      return true;
    }
    return false;
  }

  @lt('sendPercentage', 100) hasSamplePercentage!: boolean;

  @hasMany('tag', { async: true, inverse: 'newsletters' }) tags!: DS.PromiseManyArray<TagModel>;
  @hasMany('template', { async: true, inverse: 'newsletter' }) templates!: DS.PromiseManyArray<TemplateModel>;
  @hasMany('segment', { async: true, inverse: null }) segments!: DS.PromiseManyArray<SegmentModel>;
  @belongsTo('schedule', { async: true, inverse: null }) schedule!: ScheduleModel;

  @belongsTo('user-other-account', { async: false, inverse: 'createdNewsletters' })
  createdByUser!: UserOtherAccountModel;
  @belongsTo('user-other-account', { async: false, inverse: 'updatedNewsletters' })
  updatedByUser!: UserOtherAccountModel;

  tagSorting = ['name'];
  @sort('tags.content', 'tagSorting') sortedTags!: TagModel[];

  @computed('templatesReadyToSend', 'testMetric', 'tracked', 'hasConversion', 'hasSamplePercentage', 'splitTestActive')
  get readyToSchedule() {
    if (this.automaticWinner && (!this.hasSamplePercentage || !this.splitTestActive)) {
      return false;
    }

    if (this.testMetricIsConverted && !this.hasConversion) {
      return false;
    }

    if ((this.testMetricIsOpened || this.testMetricIsClicked) && !this.tracked) {
      return false;
    }

    return this.templatesReadyToSend;
  }

  @computed('readyToSchedule', 'hasRecipientsAndSubPreference', 'recipientsPerVariation')
  get readyToSend() {
    return this.readyToSchedule && this.hasRecipientsAndSubPreference && this.recipientsPerVariation >= 1;
  }

  @computed('templates.content.@each.readyToSend')
  get templatesReadyToSend() {
    let templates = (this.templates?.content as TemplateModel[] | undefined) ?? [];

    if (isNone(templates) || templates.length === 0) {
      return false;
    }

    return templates.every((template) => template.readyToSend);
  }

  @computed('channelInfo.requiresSubject', 'templates.content.@each.hasSubject')
  get templatesMissingSubject() {
    let templates = (this.templates?.content as TemplateModel[] | undefined) ?? [];

    if (!this.channelInfo.requiresSubject || isNone(templates) || templates.length === 0) {
      return false;
    }

    return !templates.every((template) => template.hasSubject);
  }

  @computed('templates.content.@each.{editor,hasSubject}')
  get templatesMissingContent() {
    let templates = (this as NewsletterModel).hasMany('templates').value() as TemplateModel[] | null;

    if (isNone(templates) || templates.length === 0) {
      return false;
    }

    return templates.some((template) => (template.editor === 'json' ? !template.hasJsonContent : !template.hasContent));
  }

  @computed('languageVariations.[]', 'templates.content.@each.{subject,body,bodyJson,language}')
  get templateLanguageVariationsDefaulted() {
    if (this.languageVariations.length < 1) {
      return false;
    }

    let defaultTemplateCache: { [key: string]: TemplateModel | undefined } = {};

    return this.languageVariations.some((template) => {
      if (defaultTemplateCache[template.testGroupId] == null) {
        defaultTemplateCache[template.testGroupId] = getDefaultTemplateForTestGroup(
          template.testGroupId,
          this.activeTemplates,
        );
      }

      let defaultTemplate = defaultTemplateCache[template.testGroupId];

      return (
        template.subject === defaultTemplate?.subject &&
        template.body === defaultTemplate?.body &&
        template.bodyJson === defaultTemplate?.bodyJson
      );
    });
  }

  @computed('templates.content.@each.{isNew,templateType}')
  get hasTemplatesWithMissingType() {
    let templates = (this as NewsletterModel).hasMany('templates').value()?.toArray() ?? [];

    return templates.some(
      (template: TemplateModel) => template == null || (!template.isNew && isBlank(template.templateType)),
    );
  }

  @computed('templates.content.@each.{isNew,id,templateType}')
  get activeTemplates() {
    if (isDestroyed(this)) {
      // this can happen when deleting a newsletter, something still tries to
      // access "activeTemplates" on it: http://localhost:4200/tests?testId=37807bb8
      return [];
    }

    let templates = (this as NewsletterModel).hasMany('templates').value()?.toArray() ?? [];

    return orderBy(
      templates.filter(
        (template: TemplateModel) =>
          template.get('isNew') !== true && isPresent(template.templateType) && template.templateType !== 'unset',
      ),
      (t) => Number(t.id),
    );
  }

  @computed('activeTemplates.@each.language')
  get testVariations() {
    return this.activeTemplates.filter((template) => isEmpty(template.language));
  }

  @computed('activeTemplates.@each.language')
  get languageVariations() {
    return this.activeTemplates.filter((template) => isPresent(template.language));
  }

  @computed('languageVariations.[]')
  get hasLanguageVariations() {
    return this.languageVariations.length > 0;
  }

  @gt('testVariations.length', 1) splitTestActive!: boolean;

  @computed('testVariations.[]')
  get splitTestAllocation() {
    let testsCount = this.testVariations.length;

    if (testsCount === 0) {
      return undefined;
    }

    return (100 / testsCount).toLocaleString('en', {
      useGrouping: false,
      maximumFractionDigits: 1,
    });
  }

  @computed('activeTemplates.@each.testGroupId')
  get testGroupsVariationNumbersMap() {
    let testGroupVariationsMap: Map<string, number> = new Map();

    let lastAssignedVariationNumber = 0;
    getTestGroupIds(this.activeTemplates).forEach((testGroupId: string) => {
      if (!testGroupVariationsMap.has(testGroupId)) {
        lastAssignedVariationNumber++;
        testGroupVariationsMap.set(testGroupId, lastAssignedVariationNumber);
      }
    });

    return testGroupVariationsMap;
  }

  @computed('activeTemplates.@each.{id,name,language,testGroupId}', 'splitTestActive', 'hasLanguageVariations')
  get activeTemplatesSelectItems() {
    return orderBy(this.activeTemplates, [(t) => Number(t.testGroupId), 'language', (t) => Number(t.id)]).map(
      (template) => {
        let splitTestVariationLabel = '';
        if (this.splitTestActive) {
          let variationNumber = this.testGroupsVariationNumbersMap.get(template.testGroupId) ?? 0;
          splitTestVariationLabel = `${COLOR_LABELS[variationNumber - 1]?.label}: `;
        }

        let languageLabel = '';
        if (this.hasLanguageVariations) {
          languageLabel = isEmpty(template.language) ? 'Default' : template.language;
          languageLabel = ` (${languageLabel})`;
        }

        return {
          value: template.id,
          label: `${splitTestVariationLabel}${template.name}${languageLabel}`,
        };
      },
    );
  }

  @computed('channel', 'subscriptionCenter.enabled')
  get channelInfo() {
    let cInfo = channelInfo(this.channel);
    if (!this.subscriptionCenter.enabled) {
      cInfo.metrics = cInfo.metrics.filter((metric) => metric !== 'topic_unsubscribed');
    }
    return cInfo;
  }
  @alias('channelInfo.icon') icon!: string;

  // channel = 'push'
  @attr('string', { defaultValue: Platform.All }) platform!: Platform;
  @alias('platform') sendToPlatform!: Platform;
  @equal('sendToPlatform', Platform.All) isPlatformDefault!: boolean;
  @not('isPlatformDefault') isPlatformNotDefault!: boolean;
  @and('sendToPlatform', 'isPlatformNotDefault') isPlatformSelected!: boolean;

  @empty('sentAt') editable!: boolean;
  @bool('scheduledTime') isScheduled!: boolean;
  @bool('sentAt') isSent!: boolean;

  // eslint-disable-next-line spellcheck/spell-checker
  @computed('isSent', 'isScheduled', 'cancelledAt', 'sending')
  get sendState() {
    let state: SendStateType = 'drafted';
    let { isSent, isScheduled, cancelledAt, sending } = this;

    if (cancelledAt) {
      state = 'stopped';
    } else if (isSent) {
      if (sending) {
        state = 'sending';
      } else {
        state = 'sent';
      }
    } else if (isScheduled) {
      state = 'scheduled';
    }

    return state;
  }

  @equal('conversion', true) hasConversion!: boolean;

  @equal('testMetric', 'converted') testMetricIsConverted!: boolean;
  @equal('testMetric', 'opened') testMetricIsOpened!: boolean;
  @equal('testMetric', 'clicked') testMetricIsClicked!: boolean;

  validConversionTimeframe = true;

  @computed('conversion', 'conversionType', 'conversionSegmentId', 'conversionEventName', 'validConversionTimeframe')
  get validConversion() {
    return isValidConversion(
      this.conversion,
      this.conversionType,
      this.conversionSegmentId as string,
      this.conversionEventName,
      this.validConversionTimeframe,
    );
  }

  @computed('scheduledAt', 'schedule.scheduledAt')
  get isScheduledSending(): boolean {
    let scheduledAt = this.scheduledTime;
    let currTimestamp = moment().unix();

    if (scheduledAt !== null) {
      return scheduledAt <= currTimestamp;
    }
    return false;
  }

  @computed('scheduledAt', 'schedule.scheduledAt')
  get scheduledTime(): number | null {
    // eslint-disable-next-line ember/no-get
    return this.scheduledAt ?? this.get('schedule')?.get('scheduledAt') ?? null;
  }

  @computed('originalScheduledAt', 'schedule.originalScheduledAt')
  get originalScheduledTime(): number | null {
    // eslint-disable-next-line ember/no-get
    return this.originalScheduledAt ?? this.get('schedule')?.get('originalScheduledAt') ?? null;
  }

  get serializedFilters() {
    let filters: SerializedLegacyFilter[] | null;
    filters = this.filters;

    filters = getUnsubscribedFilters(filters, this.sendToUnsubscribed);
    filters = getPeopleFilters(filters);
    filters = getSubscriptionTopicLegacyFilters(filters, this.subscriptionTopic, this.subscriptionCenter.enabled);

    return isNone(filters) ? filters : queryEncode(filters);
  }

  @tracked recipients: { total: number; deduped: number; created: number } = {
    total: 0,
    deduped: 0,
    created: 0,
  };

  @computed('recipients.{total,created}', 'isSent')
  get recipientsCount(): number {
    if (this.isSent) {
      if (this.hasSamplePercentage) {
        if (this.sentToRemaining) {
          return this.recipients.created;
        }

        return this.recipients.total - this.recipients.deduped;
      }

      return this.recipients.created;
    }

    return this.recipients.total;
  }

  @computed('sendPercentage', 'recipientsCount')
  get sampleRecipientsCount(): number {
    let { recipientsCount, sendPercentage } = this;
    return Math.ceil((recipientsCount * sendPercentage) / 100);
  }

  @computed('recipientsCount', 'sampleRecipientsCount')
  get remainingRecipientsCount(): number {
    return this.recipientsCount - this.sampleRecipientsCount;
  }

  @computed('hasRecipients', 'hasDeletedSubscriptionTopic')
  get hasRecipientsAndSubPreference() {
    return this.hasRecipients && !this.hasDeletedSubscriptionTopic;
  }

  @computed('recipientType', 'hasValidAudience', 'getRecipientCounts.isRunning', 'recipients.total')
  get hasRecipients() {
    return (
      isPresent(this.recipientType) &&
      this.hasValidAudience &&
      (this.getRecipientCounts.isRunning || this.recipients.total > 0)
    );
  }

  @computed('subscriptionCenter.enabled', 'subscriptionTopic', 'sendToUnsubscribed')
  get hasValidAudience() {
    if (!this.subscriptionCenter.enabled) {
      return true;
    }

    return this.subscriptionTopic != null || this.sendToUnsubscribed;
  }

  @computed('testVariations', 'sampleRecipientsCount')
  get recipientsPerVariation() {
    let templatesCount = this.testVariations.length;
    return Math.floor(this.sampleRecipientsCount / templatesCount);
  }

  @computed('filters', 'sendToEveryone')
  get recipientType() {
    let { filters, sendToEveryone } = this;
    if (sendToEveryone) {
      return 'everyone';
    } else {
      if (isEmpty(filters)) {
        return undefined;
      }

      return 'subset';
    }
  }

  async findRecipients() {
    try {
      await this.getRecipientCounts.perform();
    } catch (e) {
      this.apiErrors.report(e);
    }
  }

  @computed('recipientsCount', 'sendPercentage')
  get sendingTotalPeople() {
    return Math.round((this.recipientsCount * this.sendPercentage) / 100);
  }

  deleteTest = modelApiAction({ path: 'tests', type: 'DELETE' });

  copy = modelApiAction({
    path: 'copy',
    type: 'POST',
    before(data: { envID?: string }) {
      return {
        copy_to_env: data.envID ? Number(data.envID) : 0,
      };
    },
  });

  addTags = modelApiAction({
    path: 'tags',
    type: 'POST',
    before(tags: TagModel[]) {
      return {
        tags: tags.map((tag) => ({ id: Number(tag.id), name: tag.name })),
      };
    },

    after(this: NewsletterModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  _getRecipientCounts = modelApiAction({
    path: 'counts',
    type: 'GET',
  });

  getRecipientPrevCounts = modelApiAction({
    path: 'preview_recipient_counts',
    type: 'GET',
  });

  getRecipientCounts = task({ drop: true }, async () => {
    let result;
    try {
      if (this.isSent) {
        result = await this._getRecipientCounts({});
      } else {
        result = await this.getRecipientPrevCounts({});
      }
    } catch (error) {
      this.apiErrors.report(error);
    }

    this.recipients = {
      total: result?.recipient_count ?? 0,
      deduped: result?.deduped_count ?? 0,
      created: result?.created_count ?? 0,
    };

    return this.recipients;
  });

  async removeTag(tag: TagModel) {
    await this.api.delete(`newsletters/${this.id}/tags/${tag.id}`, {});

    this.set(
      'tags',
      (this.tags.content as TagModel[]).filter((t) => t !== tag),
    );
  }

  addTranslations = modelApiAction({
    path: 'translations',
    type: 'POST',

    before({ languages, referenceTemplate }: { languages: string[]; referenceTemplate: TemplateModel }) {
      return { languages, reference_template_id: referenceTemplate.id };
    },
    after(this: NewsletterModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  addTestGroup = modelApiAction({
    path: 'tests',
    type: 'POST',

    after(this: NewsletterModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  async deleteTestGroup(testGroupId: string) {
    let templatesPendingDeletion = getTestGroupTemplates(
      testGroupId,
      (this as NewsletterModel).hasMany('templates').value()?.slice() ?? [],
    );
    await this.api.delete(`newsletters/${this.id}/tests/${testGroupId}`);
    templatesPendingDeletion.forEach((template) => this.store.unloadRecord(template));
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    newsletter: NewsletterModel;
  }
}
